import { Eye, EyeOff } from "lucide-react"
import { type FocusEvent, useRef, useState } from "react"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"

export interface PatCredentials {
  personalAccessToken: string
}

interface PatCredentialsFormProps {
  values: PatCredentials
  onChange: (values: PatCredentials) => void
  idPrefix: string
  mode?: "connect" | "manage"
  isMasked?: boolean
  fieldLabel?: string
  helpText?: React.ReactNode
}

function SensitiveInput({
  id,
  value,
  onChange,
  placeholder,
  selectOnFocus,
  masked,
}: {
  id: string
  value: string
  onChange: (value: string) => void
  placeholder?: string
  selectOnFocus?: boolean
  masked?: boolean
}) {
  const [visible, setVisible] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  function handleFocus(e: FocusEvent<HTMLInputElement>) {
    if (selectOnFocus) {
      e.target.select()
    }
  }

  return (
    <div className="relative">
      <Input
        ref={inputRef}
        id={id}
        type={visible ? "text" : "password"}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={handleFocus}
        autoComplete="off"
        className="pr-9"
      />
      {!masked && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="text-muted-foreground hover:text-foreground absolute top-0 right-0 h-9 w-9"
          onClick={() => setVisible((v) => !v)}
          tabIndex={-1}
          aria-label={visible ? "Hide value" : "Show value"}
        >
          {visible ? (
            <EyeOff className="h-4 w-4" />
          ) : (
            <Eye className="h-4 w-4" />
          )}
        </Button>
      )}
    </div>
  )
}

export function PatCredentialsForm({
  values,
  onChange,
  idPrefix,
  mode = "connect",
  isMasked,
  fieldLabel = "Personal Access Token",
  helpText,
}: PatCredentialsFormProps) {
  const isManage = mode === "manage"

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-pat`}>{fieldLabel}</Label>
        <SensitiveInput
          id={`${idPrefix}-pat`}
          value={values.personalAccessToken}
          onChange={(v) => onChange({ personalAccessToken: v })}
          selectOnFocus={isManage}
          masked={isMasked}
        />
        {helpText && (
          <p className="text-muted-foreground text-xs">{helpText}</p>
        )}
      </div>
    </div>
  )
}
